import type {
  Package as WebPackage,
  Product as WebProduct,
  CustomerInfo as WebCustomerInfo,
  EntitlementInfo as WebEntitlementInfo,
  PricingPhase as WebPricingPhase
} from '@revenuecat/purchases-js';
import {
  CustomerInfo,
  Entitlement,
  EntitlementPeriodType,
  IntroPrice,
  SubscriptionOption,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import { mapEffectiveOffer } from './effective-offer';

/**
 * Maps RevenueCat Web SDK (purchases-js) objects
 * to our PurchaseProvider interface types.
 *
 * Key differences from the Capacitor SDK:
 * - Prices are Price objects (amountMicros, currency)
 * - Dates are Date objects (not ISO strings)
 * - Package uses webBillingProduct (not product)
 */
export class WebSdkMapper {

  mapIntroPrice(
    phase: WebPricingPhase
  ): IntroPrice {
    return {
      price: phase.price
        ? phase.price.amountMicros / 1_000_000
        : 0,
      priceString: phase.price
        ? phase.price.formattedPrice
        : 'Free',
      period: phase.periodDuration ?? null
    };
  }

  mapProduct(
    webProduct: WebProduct,
    offeringIdentifier?: string | null
  ): SubscriptionProduct {
    const subOpt =
      webProduct.defaultSubscriptionOption;
    let defaultOption: SubscriptionOption | null =
      null;
    if (subOpt) {
      defaultOption = {
        introPhase: subOpt.introPrice
          ? {
              price: subOpt.introPrice.price
                ? {
                    amountMicros:
                      subOpt.introPrice.price
                        .amountMicros,
                    formatted:
                      subOpt.introPrice.price
                        .formattedPrice
                  }
                : undefined,
              billingPeriod:
                subOpt.introPrice.periodDuration
                  ? {
                      iso8601:
                        subOpt.introPrice
                          .periodDuration
                    }
                  : undefined
            }
          : null,
        fullPricePhase: subOpt.base
          ? {
              price: {
                amountMicros:
                  subOpt.base.price?.amountMicros
                  ?? 0,
                formatted:
                  subOpt.base.price?.formattedPrice
                  ?? ''
              },
              billingPeriod:
                subOpt.base.periodDuration
                  ? {
                      iso8601:
                        subOpt.base.periodDuration
                    }
                  : undefined
            }
          : null
      };
    }

    // Use the free trial phase if available,
    // otherwise fall back to intro price phase
    const trialPhase = webProduct.freeTrialPhase;
    const introPhase = webProduct.introPricePhase;
    const introSource = trialPhase ?? introPhase;

    // Get pricePerMonth from the base phase
    const basePricePerMonth =
      subOpt?.base?.pricePerMonth;

    const product: SubscriptionProduct = {
      identifier: webProduct.identifier,
      title: webProduct.title,
      price:
        webProduct.price.amountMicros / 1_000_000,
      priceString:
        webProduct.price.formattedPrice,
      currencyCode: webProduct.price.currency,
      productType:
        String(webProduct.productType),
      subscriptionPeriod:
        webProduct.normalPeriodDuration ?? null,
      pricePerMonthString:
        basePricePerMonth?.formattedPrice ?? null,
      introPrice: introSource
        ? this.mapIntroPrice(introSource)
        : null,
      defaultOption,
      offeringIdentifier: offeringIdentifier ?? null
    };
    // Web Billing has no eligibility concept — the
    // synthesised defaultOption phase is the only source.
    product.effectiveOffer = mapEffectiveOffer(product);
    return product;
  }

  mapPackage(
    pkg: WebPackage,
    offeringIdentifier?: string | null
  ): SubscriptionPackage {
    return {
      identifier: pkg.identifier,
      product: this.mapProduct(
        pkg.webBillingProduct, offeringIdentifier
      )
    };
  }

  mapEntitlement(
    ent: WebEntitlementInfo
  ): Entitlement {
    return {
      identifier: ent.identifier,
      isActive: ent.isActive,
      willRenew: ent.willRenew,
      productIdentifier: ent.productIdentifier,
      latestPurchaseDate:
        ent.latestPurchaseDate?.toISOString(),
      expirationDate:
        ent.expirationDate?.toISOString() ?? null,
      // purchases-js reports periodType lowercase;
      // normalise to match the Capacitor SDK
      periodType: ent.periodType
        ? (ent.periodType.toUpperCase() as
          EntitlementPeriodType)
        : undefined
      // productPlanIdentifier intentionally omitted —
      // no such concept in purchases-js/Web Billing.
    };
  }

  mapEntitlementRecord(
    record: Record<string, WebEntitlementInfo>
  ): Record<string, Entitlement> {
    const result: Record<string, Entitlement> = {};
    for (const [key, val] of Object.entries(
      record
    )) {
      result[key] = this.mapEntitlement(val);
    }
    return result;
  }

  mapCustomerInfo(
    info: WebCustomerInfo
  ): CustomerInfo {
    return {
      entitlements: {
        active: this.mapEntitlementRecord(
          info.entitlements.active
        ),
        all: this.mapEntitlementRecord(
          info.entitlements.all
        )
      },
      originalPurchaseDate:
        info.originalPurchaseDate?.toISOString()
        ?? null,
      latestExpirationDate: null
    };
  }
}
